import { promptSections } from './ai/prompt-file';

/**
 * T-50: whether the AI builder only gives feedback on a task (a plan, a fuller spec, questions; no code,
 * and the task's column stays) or builds it. Jev picks when the task is assigned to the AI builder or
 * someone @mentions it, with the question in factory/ai-prompts/run-mode.md. Pure, so it's tested
 * without a call (worker/src/services/runs/mode.ts makes it).
 */

export const RUN_MODES = ['feedback', 'implement'] as const;
export type RunMode = (typeof RUN_MODES)[number];

/** The flows that only give feedback. */
export const FEEDBACK_FLOW_IDS: readonly string[] = ['feedback', 'feedback-follow-up'];

export function isFeedbackFlow(flowId: string | null | undefined): boolean {
  return !!flowId && FEEDBACK_FLOW_IDS.includes(flowId);
}

/** Below this, Jev's answer is left out and the fallback is used. */
export const RUN_MODE_MIN_CONFIDENCE = 0.6;

export interface RunModeQuestion {
  type: 'choice';
  instructions: string;
  criteria: Record<RunMode, string>;
}

/** The prompt file's `## mode` section as Jev's question; throws naming what's missing, so a bad file fails its test. */
export function runModeQuestions(body: string): { mode: RunModeQuestion } {
  const section = promptSections(body).mode;
  if (!section?.instructions) throw new Error('run-mode.md: the "## mode" section or its instructions are missing');
  const map = Object.fromEntries(section.criteria.map((c) => [c.key, c.text]));
  const missing = RUN_MODES.filter((m) => !map[m]);
  if (missing.length) throw new Error(`run-mode.md: "## mode" has no line for ${missing.join(', ')}`);
  return { mode: { type: 'choice', instructions: section.instructions, criteria: { feedback: map.feedback, implement: map.implement } } };
}

/**
 * When Jev can't say: stay in feedback after a feedback run (until someone asks to build), else build,
 * as the AI builder always did.
 */
export function fallbackRunMode(lastMode: RunMode | null): RunMode {
  return lastMode === 'feedback' ? 'feedback' : 'implement';
}

/** Jev's pick when it's sure enough, else null. */
export function interpretRunMode(answers: Record<string, unknown>, minConfidence = RUN_MODE_MIN_CONFIDENCE): { mode: RunMode; confidence: number } | null {
  const a = (answers.mode ?? {}) as { choice?: unknown; confidence?: unknown };
  const confidence = typeof a.confidence === 'number' ? a.confidence : 0;
  if (typeof a.choice !== 'string' || !(RUN_MODES as readonly string[]).includes(a.choice) || confidence < minConfidence) return null;
  return { mode: a.choice as RunMode, confidence };
}
